import React, { useState } from "react";
import { Alert, Button, Card, Col, Form, Row } from "react-bootstrap";

type FilingType = "NONE" | "SR22" | "FR44";
type VehicleUse = "commute" | "pleasure" | "business" | "rideshare";
type CoveragePref = "state_min" | "standard" | "full_coverage" | "unsure";

interface DuiQuotePayload {
  // Personal
  firstName: string;
  lastName: string;
  dob?: string;
  email: string;
  phone: string;
  state: string;
  zip: string;

  // License & DUI
  licenseNumber?: string;
  duiCount: number;
  duiMostRecentDate?: string;
  filingType: FilingType;
  filingState?: string;

  // Vehicle (optional)
  ownsVehicle: boolean;
  vehicleYear?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vin?: string;
  estAnnualMileage?: string;
  vehicleUse?: VehicleUse;

  // Coverage
  coveragePreference: CoveragePref;

  // Other
  notes?: string;
  consent: boolean;
  terms: boolean;
}

interface DuiQuickQuoteFormProps {
  clientVersion: string;
  testMode: boolean;
  apiUrl: string;
  themeCardShadow?: string;
}


export const API_URL = "aws.api.url";
export const imagePath = "insurance/images/DUI-Insurance/";

export const heroImages = [
  { src: "AdobeStock_1463253262_Preview-FLIP.jpg", alt: "Driving" },
  { src: "AdobeStock_370848671_Preview.jpeg", alt: "Driving" },
  { src: "AdobeStock_1360258027_Preview-FLIP.jpg", alt: "Driving" },
  { src: "AdobeStock_802630377_Preview-FLIP.jpg", alt: "Driving" },
  { src: "AdobeStock_571265185_Preview.jpeg", alt: "driving" },
  // { src: "AdobeStock_1596964140_CAT-COOL-Preview-FLIP.jpg", alt: "Cool CAT" },
];



const isValidEmail = (v: string) => /.+@.+\..+/.test(v);
const digitsOnly = (v: string) => v.replace(/\D+/g, "");
const states = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN",
  "MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA",
  "WA","WV","WI","WY","DC",
];

export const DUI_Quote_FORM_v01: React.FC<DuiQuickQuoteFormProps> = ({
  clientVersion,
  testMode,
  apiUrl,
  themeCardShadow = "0 8px 24px rgba(0,0,0,0.08)",
}) => {
  // Personal
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [dob, setDob]             = useState(""); // yyyy-mm-dd
  const [email, setEmail]         = useState("");
  const [phone, setPhone]         = useState("");
  const [state, setState]         = useState("CO");
  const [zip, setZip]             = useState("");

  // License & DUI
  const [licenseNumber, setLicenseNumber]           = useState("");
  const [duiCount, setDuiCount]                     = useState<number>(1);
  const [duiMostRecentDate, setDuiMostRecentDate]   = useState("");
  const [filingType, setFilingType]                 = useState<FilingType>("NONE");
  const [filingState, setFilingState]               = useState("");

  // Vehicle
  const [ownsVehicle, setOwnsVehicle]   = useState<boolean>(false);
  const [vehicleYear, setVehicleYear]   = useState("");
  const [vehicleMake, setVehicleMake]   = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [vin, setVin]                   = useState("");
  const [estAnnualMileage, setEstAnnualMileage] = useState("");
  const [vehicleUse, setVehicleUse]     = useState<VehicleUse>("commute");

  // Coverage
  const [coveragePreference, setCoveragePreference] =
    useState<CoveragePref>("standard");

  // Other
  const [notes, setNotes]       = useState("");
  const [consent, setConsent]   = useState(true);
  const [terms, setTerms]       = useState(false);

  // UI
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [success, setSuccess]   = useState<string | null>(null);

  const resetAlerts = () => {
    setError(null);
    setSuccess(null);
  };

  const validate = (): string | null => {
    if (!firstName) return "Please enter your first name.";
    if (!lastName) return "Please enter your last name.";
    if (!email || !isValidEmail(email)) return "Please enter a valid email address.";
    if (!phone || digitsOnly(phone).length < 10) return "Please enter a valid phone number.";
    if (!state) return "Please select your state.";
    if (!zip || digitsOnly(zip).length < 5) return "Please enter a valid 5-digit ZIP code.";
    if (duiCount < 0) return "DUI count cannot be negative.";

    // Filing validations
    if (filingType !== "NONE") {
      if (!filingState) return "Please select the filing state.";
      if (filingType === "FR44" && filingState !== "VA" && filingState !== "FL") {
        return "FR-44 filings generally apply to Virginia or Florida. Please select SR-22 or change filing state.";
      }
    }

    // If they own a vehicle, require basic details
    if (ownsVehicle) {
      if (!vehicleYear || !vehicleMake || !vehicleModel) {
        return "Please enter year, make, and model for your vehicle.";
      }
      if (vin && vin.length > 0 && vin.length < 11) {
        return "VIN should be at least 11 characters (17 preferred).";
      }
    }

    if (!terms) return "Please accept the Terms to continue.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    resetAlerts();

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    const payload: DuiQuotePayload = {
      // Personal
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dob: dob || undefined,
      email: email.trim(),
      phone: digitsOnly(phone),
      state,
      zip: digitsOnly(zip).slice(0, 5),

      // License & DUI
      licenseNumber: licenseNumber.trim() || undefined,
      duiCount: Number(duiCount),
      duiMostRecentDate: duiMostRecentDate || undefined,
      filingType,
      filingState: filingType === "NONE" ? undefined : filingState || undefined,

      // Vehicle
      ownsVehicle,
      vehicleYear: ownsVehicle ? vehicleYear.trim() || undefined : undefined,
      vehicleMake: ownsVehicle ? vehicleMake.trim() || undefined : undefined,
      vehicleModel: ownsVehicle ? vehicleModel.trim() || undefined : undefined,
      vin: ownsVehicle ? vin.trim() || undefined : undefined,
      estAnnualMileage: ownsVehicle ? digitsOnly(estAnnualMileage) || undefined : undefined,
      vehicleUse: ownsVehicle ? vehicleUse : undefined,

      // Coverage
      coveragePreference,

      // Other
      notes: notes.trim() || undefined,
      consent,
      terms,
    };

    try {
      setBusy(true);
      if (testMode) {
        console.log("DUI Quote Payload →", payload, { clientVersion });
        await new Promise((r) => setTimeout(r, 700));
        setSuccess("Thanks! We'll email your DUI quote options soon.");
      } else {
        const res = await fetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        setSuccess("Thanks! We'll email your DUI quote options soon.");
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ boxShadow: themeCardShadow, borderRadius: 14 }}>
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="m-0">DUI Insurance — Quick Quote</h5>
          <small className="text-muted">{clientVersion}{testMode ? " · Test" : ""}</small>
        </div>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {success && <Alert variant="success" className="py-2">{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            {/* Personal */}
            <Col md={6}>
              <Form.Label>First Name</Form.Label>
              <Form.Control value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Last Name</Form.Label>
              <Form.Control value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                placeholder="you@example.com"
                onChange={(e) => setEmail(e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Phone (required)</Form.Label>
              <Form.Control
                inputMode="tel"
                placeholder="(555) 555-5555"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </Col>
            <Col md={6}>
              <Form.Label>Date of Birth (optional)</Form.Label>
              <Form.Control type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
            </Col>
            <Col md={3}>
              <Form.Label>State</Form.Label>
              <Form.Select value={state} onChange={(e) => setState(e.target.value)}>
                {states.map((s) => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>ZIP</Form.Label>
              <Form.Control
                inputMode="numeric"
                placeholder="80301"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </Col>

            {/* DUI / License */}
            <Col md={6}>
              <Form.Label>Driver’s License # (optional)</Form.Label>
              <Form.Control value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} />
            </Col>
            <Col md={3}>
              <Form.Label>DUI Count</Form.Label>
              <Form.Select
                value={duiCount}
                onChange={(e) => setDuiCount(Number(e.target.value))}
              >
                {[0,1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Most Recent DUI (optional)</Form.Label>
              <Form.Control
                type="date"
                value={duiMostRecentDate}
                onChange={(e) => setDuiMostRecentDate(e.target.value)}
              />
            </Col>

            <Col md={4}>
              <Form.Label>Filing Type</Form.Label>
              <Form.Select value={filingType} onChange={(e) => setFilingType(e.target.value as FilingType)}>
                <option value="NONE">No Filing Needed</option>
                <option value="SR22">SR-22</option>
                <option value="FR44">FR-44 (VA/FL)</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Filing State</Form.Label>
              <Form.Select
                value={filingState}
                onChange={(e) => setFilingState(e.target.value)}
                disabled={filingType === "NONE"}
              >
                <option value="">{filingType === "NONE" ? "N/A" : "Select State"}</option>
                {states.map((s) => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Col>

            {/* Vehicle */}
            <Col xs={12}>
              <Form.Check
                type="switch"
                id="owns-vehicle"
                label="I currently own a vehicle"
                checked={ownsVehicle}
                onChange={(e) => setOwnsVehicle(e.currentTarget.checked)}
              />
            </Col>

            {ownsVehicle && (
              <>
                <Col md={3}>
                  <Form.Label>Year</Form.Label>
                  <Form.Control value={vehicleYear} onChange={(e) => setVehicleYear(e.target.value)} />
                </Col>
                <Col md={4}>
                  <Form.Label>Make</Form.Label>
                  <Form.Control value={vehicleMake} onChange={(e) => setVehicleMake(e.target.value)} />
                </Col>
                <Col md={5}>
                  <Form.Label>Model</Form.Label>
                  <Form.Control value={vehicleModel} onChange={(e) => setVehicleModel(e.target.value)} />
                </Col>
                <Col md={6}>
                  <Form.Label>VIN (optional)</Form.Label>
                  <Form.Control value={vin} onChange={(e) => setVin(e.target.value)} />
                </Col>
                <Col md={3}>
                  <Form.Label>Est. Annual Miles</Form.Label>
                  <Form.Control
                    inputMode="numeric"
                    value={estAnnualMileage}
                    onChange={(e) => setEstAnnualMileage(e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label>Use</Form.Label>
                  <Form.Select value={vehicleUse} onChange={(e) => setVehicleUse(e.target.value as VehicleUse)}>
                    <option value="commute">Commute</option>
                    <option value="pleasure">Pleasure</option>
                    <option value="business">Business</option>
                    <option value="rideshare">Rideshare</option>
                  </Form.Select>
                </Col>
              </>
            )}

            {/* Coverage */}
            <Col md={6}>
              <Form.Label>Coverage Preference</Form.Label>
              <Form.Select
                value={coveragePreference}
                onChange={(e) => setCoveragePreference(e.target.value as CoveragePref)}
              >
                <option value="state_min">State Minimum</option>
                <option value="standard">Standard</option>
                <option value="full_coverage">Full Coverage</option>
                <option value="unsure">I'm not sure yet</option>
              </Form.Select>
            </Col>

            {/* Notes & Consents */}
            <Col xs={12}>
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything we should know? (tickets, prior insurance, special requests)"
              />
            </Col>

            <Col xs={12}>
              <Form.Check
                id="consent"
                type="checkbox"
                label="I agree to be contacted about my quote (email/SMS)."
                checked={consent}
                onChange={(e) => setConsent(e.currentTarget.checked)}
              />
              <Form.Check
                id="terms"
                type="checkbox"
                label={<span>I accept the <a href="#">Terms</a> & <a href="#">Privacy</a>.</span>}
                checked={terms}
                onChange={(e) => setTerms(e.currentTarget.checked)}
              />
            </Col>

            <Col xs={12} className="d-grid">
              <Button type="submit" size="lg" variant="success" disabled={busy}>
                {busy ? "Submitting…" : "Get My DUI Quote"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default DUI_Quote_FORM_v01;
